/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useEffect } from 'react';

import { useAtom } from 'jotai';
import { useRouter } from 'next/router';

import { fbQueryParamsKeysToIgnore } from '@/api/meilisearch/models';
import {
  categories,
  extractCategoryFromHelper,
  getGovernorateId,
  governorates,
  getProductTypeId,
} from '@/lib/helpers';

import { objectNotEmpty } from './useElasticSearchServer';
import {
  OptionsParamsAtom,
  RangeParamsAtom,
  SubCategoryAtom,
  ParamsListAtom,
  CategoryAtom,
  SelectedPriceAtom,
  FilterAtom,
  SearchQueryAtom,
  Filter,
  SelectedParamsRangeValue,
  SelectedPrice,
  GovernorateSelect,
  GovernorateAtom,
  DelegationAtom,
  DelegationListAtom,
  SubCategoryListAtom,
  OptionParam,
  ProductTypeAtom,
} from '../store/store';
import { ProductTypeEnum } from '../types';

const useFilterSelect = (
  verifySaveSearchUnique: (f: Filter, q: string) => {}
) => {
  const { query } = useRouter();
  const [optionsParams, updateOptionsParams] = useAtom(OptionsParamsAtom);
  const [rangeParams, updateRangeParams] = useAtom(RangeParamsAtom);
  const [category, updateCategory] = useAtom(CategoryAtom);
  const [subCategory, updateSubCategory] = useAtom(SubCategoryAtom);
  const [subCategoryList, setSubCategoryList] = useAtom(SubCategoryListAtom);
  const [selectedPrice, updateSelectedPrice] = useAtom(SelectedPriceAtom);
  const [productType, updateProductType] = useAtom(ProductTypeAtom);
  const [paramsList, updateParamsList] = useAtom(ParamsListAtom);

  const [governorate, setGovernorate] = useAtom(GovernorateAtom);
  const [delegation, updateDelegation] = useAtom(DelegationAtom);
  const [delegationList, setDelegationList] = useAtom(DelegationListAtom);
  const [filter, setFilter]: any = useAtom(FilterAtom);
  const [, setSearchQueryStore] = useAtom(SearchQueryAtom);
  // const [searchedListingsStore, setSearchedListingsStore] =
  //   useAtom(searchedListingsAtom);
  useEffect(() => {
    const newFilter: Filter = {};

    Object.keys(query).forEach((key: string) => {
      if (
        key === 'v' ||
        key === 'page' ||
        fbQueryParamsKeysToIgnore.includes(key)
      ) {
        // pass
      } else if (key === 'q') {
        setSearchQueryStore(query[key] as string);
      } else if (key === 'minPrice') {
        const minPriceQuery = query[key] as string;
        updateSelectedPrice({
          type: 'set',
          data: {
            min: minPriceQuery as string,
          },
        });
        newFilter.minPrice =
          minPriceQuery !== '' ? Number(minPriceQuery) : undefined;
      } else if (key === 'maxPrice') {
        const maxPriceQuery = query[key] as string;
        updateSelectedPrice({
          type: 'set',
          data: {
            max: maxPriceQuery as string,
          },
        });
        newFilter.maxPrice =
          maxPriceQuery !== '' ? Number(maxPriceQuery) : undefined;
      } else if (key === 'category') {
        const foundCategory = extractCategoryFromHelper(query[key] as string);

        updateCategory({
          type: 'set',
          selectedCategory: {
            id: foundCategory?.id,
            name: foundCategory?.name,
          },
        });
        newFilter.categoryId = foundCategory?.id;
        newFilter.categoryName = foundCategory?.name;
      } else if (key === 'subCategory') {
        const foundCategory = extractCategoryFromHelper(query[key] as string);

        updateSubCategory({
          type: 'set',
          selectedCategory: {
            id: foundCategory?.id,
            name: foundCategory?.name,
          },
        });
        newFilter.subCategoryName = foundCategory?.name;
        newFilter.subCategoryIds = [foundCategory?.id!];
      } else if (key === 'governorate') {
        const governorateId = getGovernorateId(query[key] as string);
        if (governorateId !== null)
          setGovernorate({ id: governorateId, name: query[key] as string });
        newFilter.governorate = query[key] as string;
      } else if (key === 'delegation') {
        if (query[key]) {
          const newFilterDelegation: string[] = [];
          const delegationsValues = (query[key] as string).split(',');
          delegationsValues.forEach((delegationsValue) => {
            const delegationId = delegationsValue;
            if (delegationId !== null) {
              newFilterDelegation.push(delegationId);
            }
          });
          updateDelegation({
            type: 'init',
            values: newFilterDelegation,
          });
          newFilter.delegation = newFilterDelegation;
        }
      } else if (key === 'productType') {
        if (query[key]) {
          const newFilterProductType: number[] = [];
          const productTypesValues = (query[key] as string).split(',');
          productTypesValues.forEach((productTypesValue) => {
            const productTypeId = getProductTypeId(productTypesValue as string);
            if (productTypeId !== null) {
              newFilterProductType.push(productTypeId);
            }
          });
          updateProductType({
            type: 'init',
            values: newFilterProductType,
          });
          newFilter.productType = newFilterProductType;
        }
      } else if (key.startsWith('min')) {
        const keyRes = key.substring(3);
        const newRangeParam: SelectedParamsRangeValue = {
          name: keyRes,
          selectedMin: query[key] as string,
        };
        updateRangeParams({ type: 'setMin', data: newRangeParam });
        newFilter.rangeAdParams = {
          ...newFilter.rangeAdParams,
          [keyRes]: {
            name: keyRes,
            selectedMin: newRangeParam.selectedMin,
          },
        };
      } else if (key.startsWith('max')) {
        const keyRes = key.substring(3);
        const newRangeParam: SelectedParamsRangeValue = {
          name: keyRes,
          selectedMax: query[key] as string,
        };
        updateRangeParams({ type: 'setMax', data: newRangeParam });
        if (
          newFilter.rangeAdParams !== undefined &&
          newFilter.rangeAdParams[keyRes] !== undefined &&
          newFilter.rangeAdParams[keyRes]!.selectedMin !== undefined
        ) {
          newRangeParam.selectedMin =
            newFilter.rangeAdParams[keyRes]!.selectedMin;
        }

        newFilter.rangeAdParams = {
          ...newFilter.rangeAdParams,
          [keyRes]: newRangeParam,
        };
      } else if (key === 'after_timestamp') {
        // if (query[key])
        //   newFilter.afterTimestamp = parseInt((query[key] as string) || '', 2);
      } else {
        const newOptionParam: OptionParam = {
          name: key,
          value: query[key] as string,
        };
        updateOptionsParams(newOptionParam);
        newFilter.adParams = {
          ...newFilter.adParams,
          [newOptionParam.name]: newOptionParam.value,
        };
      }
    });

    // Setting this here so that when someone navigate out and back to `/search`
    // his filters get updated
    setFilter(newFilter);

    verifySaveSearchUnique(newFilter, query.q as string);
    // (*LEGACY*) we now fetch the first listings in SSR
    // This is the case where the user is first visiting the `/search` page
    // and we want to init search from values from query params
    /* const firstConnect =
      searchedListingsStore.hits.length === 0 &&
      searchedListingsStore.isDefaultListings;
    if (firstConnect) {
      initSearch(searchedListingsStore, setSearchedListingsStore, {
        type: 'filter',
        filter: newFilter,
        query: newSearchQuery,
      });
    } */
  }, [query]);

  const handlePriceRange = (newSelectedPrice: SelectedPrice) => {
    updateSelectedPrice({ type: 'set', data: newSelectedPrice });

    if (objectNotEmpty(newSelectedPrice)) {
      const newFilter = filter;
      if (newSelectedPrice.min !== undefined && newSelectedPrice.min !== '')
        newFilter.minPrice = Number(newSelectedPrice.min);
      else newFilter.minPrice = undefined;

      if (newSelectedPrice.max !== undefined && newSelectedPrice.max !== '')
        newFilter.maxPrice = Number(newSelectedPrice.max);
      else newFilter.maxPrice = undefined;

      setFilter(newFilter);
    }
  };

  const handleGovernorate = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedGovernorate: GovernorateSelect | null =
      governorates.find((g) => g.id === event.target.value) || null;

    setGovernorate(selectedGovernorate);
    if (selectedGovernorate !== null) {
      setFilter({
        ...filter,
        governorate: selectedGovernorate.name,
        delegation: undefined,
      });
    } else {
      setFilter({
        ...filter,
        governorate: undefined,
        delegation: undefined,
      });
    }
  };

  const handleDelegation = (value: string) => {
    updateDelegation({
      type: 'update',
      value,
    });

    const newFilter = {
      ...filter,
      delegation:
        filter?.delegation && filter?.delegation?.includes(value as string)
          ? filter?.delegation?.filter((elem: any) => elem !== value)
          : [...(filter?.delegation || ([] as [])), value],
    };

    setFilter(newFilter as Filter);
  };

  const handleCategory = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedCategory =
      categories.find((c) => c.id === event.target.value) || null;

    if (selectedCategory !== null) {
      updateCategory({
        type: 'set',
        selectedCategory,
      });
    } else {
      updateCategory({ type: 'empty' });
    }

    if (selectedCategory !== null && objectNotEmpty(selectedCategory)) {
      setFilter({
        ...filter,
        categoryName: selectedCategory.name,
        categoryId: selectedCategory.id,
        subCategoryName: undefined,
        subCategoryIds: undefined,
        adParams: undefined,
        rangeAdParams: undefined,
      });
    } else {
      setFilter({
        ...filter,
        categoryName: undefined,
        categoryId: undefined,
        subCategoryName: undefined,
        subCategoryIds: undefined,
        adParams: undefined,
        rangeAdParams: undefined,
      });
    }
  };

  const handleSubCategory = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedSubCategory =
      categories.find((c) => c.id === event.target.value) || null;

    if (selectedSubCategory !== null) {
      updateSubCategory({
        type: 'set',
        selectedCategory: selectedSubCategory,
      });
    } else {
      updateSubCategory({ type: 'empty' });
    }

    if (selectedSubCategory !== null && objectNotEmpty(selectedSubCategory)) {
      setFilter({
        ...filter,
        subCategoryName: selectedSubCategory.name,
        subCategoryIds: [selectedSubCategory.id],
        adParams: undefined,
        rangeAdParams: undefined,
      });
    } else {
      setFilter({
        ...filter,
        subCategoryName: undefined,
        subCategoryIds: undefined,
        adParams: undefined,
        rangeAdParams: undefined,
      });
    }
  };

  const handleOptionsParams = (
    event: ChangeEvent<HTMLSelectElement>,
    name: string
  ) => {
    const newOptionParam: OptionParam = {
      name,
      value: event.target.value,
    };
    updateOptionsParams(newOptionParam);

    const newOptionsParams = {
      ...optionsParams,
      [newOptionParam.name]: newOptionParam.value,
    };
    if (newOptionParam.value === '')
      delete newOptionsParams[newOptionParam.name];

    if (objectNotEmpty(newOptionsParams)) {
      setFilter({
        ...filter,
        adParams: newOptionsParams,
      });
    } else {
      setFilter({
        ...filter,
        adParams: undefined,
      });
    }
  };
  const handleOptionsParamsInput = (
    event: ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const newOptionParam: OptionParam = {
      name,
      value: event.target.value,
    };
    const isChecked = event.target.checked;

    if (!isChecked) {
      delete optionsParams[newOptionParam.name];
      setFilter({
        ...filter,
        adParams: optionsParams,
      });
      return;
    }

    const newOptionsParams = {
      ...optionsParams,
      [newOptionParam.name]: newOptionParam.value,
    };

    if (isChecked) {
      updateOptionsParams(newOptionParam);

      if (objectNotEmpty(newOptionsParams)) {
        setFilter({
          ...filter,
          adParams: newOptionsParams,
        });
      } else {
        setFilter({
          ...filter,
          adParams: undefined,
        });
      }
    }

    console.log('filter', filter);
  };

  const handleRangeParams = (
    type: 'setMin' | 'setMax',
    rangeParamName: string,
    newValue: string
  ) => {
    let newRangeParam: SelectedParamsRangeValue;
    if (type === 'setMin') {
      newRangeParam = {
        name: rangeParamName,
        selectedMin: newValue,
      };
      updateRangeParams({
        type,
        data: newRangeParam,
      });
    } else {
      newRangeParam = {
        name: rangeParamName,
        selectedMax: newValue,
      };
      updateRangeParams({
        type,
        data: newRangeParam,
      });
    }

    // Only use this in setFilter()
    const newRangeParams = {
      ...rangeParams,
      [rangeParamName]: {
        ...rangeParams[rangeParamName],
        ...newRangeParam,
      },
    };

    if (objectNotEmpty(newRangeParams)) {
      setFilter({
        ...filter,
        rangeAdParams: newRangeParams,
      });
    } else {
      setFilter({
        ...filter,
        rangeAdParams: undefined,
      });
    }
  };

  const handleProductType = (value: ProductTypeEnum | undefined) => {
    updateProductType({
      type: 'update',
      value: value || ProductTypeEnum.OCCASION,
    });

    const newFilter = {
      ...filter,
      productType:
        filter?.productType && filter?.productType?.includes(value as number)
          ? filter?.productType?.filter((elem: any) => elem !== value)
          : [...(filter?.productType || ([] as [])), value],
    };

    setFilter(newFilter as Filter);
  };

  const handleResetFilter = () => {
    setGovernorate(null);
    updateDelegation({ type: 'reset' });
    setDelegationList([]);
    updateCategory({ type: 'empty' });
    updateSubCategory({ type: 'empty' });
    setSubCategoryList([]);
    updateParamsList([]);
    setFilter({});
    setSearchQueryStore('');
    updateSelectedPrice({ type: 'empty' });
    updateProductType({ type: 'reset' });
  };

  // Do a cleanup on component's unmount
  useEffect(
    () => () => {
      handleResetFilter();
    },
    []
  );

  return {
    selectedPrice,
    handlePriceRange,
    governorate,
    handleGovernorate,
    delegation,
    handleDelegation,
    delegationList,
    category,
    handleCategory,
    updateCategory,
    subCategory,
    handleSubCategory,
    subCategoryList,
    optionsParams,
    handleOptionsParams,
    rangeParams,
    handleRangeParams,
    paramsList,
    handleResetFilter,
    handleProductType,
    productType,
    handleOptionsParamsInput,
  };
};

export default useFilterSelect;
